import {
  Box,
  Divider,
  FormLabel,
  Icon,
  Input,
  SimpleGrid,
  Image,
  Flex,
  Text,
  GridItem,
  InputRightElement,
  useDisclosure,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputLeftAddon,
  PopoverBody,
  PopoverCloseButton,
  PopoverHeader,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { FieldArray, Form, Formik } from 'formik';
import { isDate } from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'react-image-lightbox';
import { useNavigate } from 'react-router-dom';
import 'react-image-lightbox/style.css';

import { IconPicture, IconClose, IconPlus, IconInfo } from 'assets/icons';
import Button from 'components/Button';
import {
  FormikCheckbox,
  FormikDatepicker,
  FormikInput,
  FormikSelect,
  // FormikRadio,
  FormikSelectAsyncPaginate,
  FormikTextArea,
} from 'components/Formik';
import { ControllableModal } from 'components/Modal';
import Popover from 'components/Popover';
import Title from 'components/Title';
import Tooltip from 'components/Tooltip';
import { lunchBreakDuration } from 'containers/TimesheetMileage/shared/options';
import {
  CLOCKIN_FUTURE_DATE_ALLOWED_IDS,
  MONTH_DAY_YEAR_TIME_FORMAT,
  MONTH_DAY_YEAR_TIME_POST_FORMAT,
} from 'global/appConstants';
import FEATURES from 'global/features';
import { TIMESHEET_DISPLAY_TYPES } from 'global/options';
import { PLANS } from 'global/plans';
import { useFetchAllBreakRules } from 'hooks/breakRules/useFetchAllBreakRules';
import { useDeleteBreaks } from 'hooks/breaks/useDeleteBreaks';
import { ICustomFieldItem } from 'hooks/customField/useFetchPaginatedCustomFields';
import { fetchPaginatedEmployee } from 'hooks/employee/useFetchPaginatedEmployee';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { fetchPaginatedTasks } from 'hooks/task/useFetchPaginatedTasks';
import { useDeleteTimesheetImage } from 'hooks/timesheet/useDeleteTimesheetImage';
import { useRecalculateMileage } from 'hooks/timesheet/useRecalculateMileage';
import { ISaveTimesheetData, useSaveTimesheet } from 'hooks/timesheet/useSaveTimesheet';
import { useUpdateImageCaption } from 'hooks/timesheet/useUpdateImageCaption';
import { useFeatureList } from 'store/useFeatureStore';
import { useCompanyInfo } from 'store/useUserStore';
import { transformCustomFieldsToPostData } from 'utils/customFieldsUtils';
import {
  getAsyncSelectLoadOptions,
  getDistanceInCompanyFormat,
  postDistanceInMiles,
  secondsToMS,
  isNumber,
} from 'utils/helpers';
import toaster from 'utils/toaster';

import { dateBetweenTwoDates, getDaysRange, removeDaysRange } from './../helpers';
import { initialTimesheetFormData, timesheetFormValidationSchema } from './schema';
import TimesheetCustomField from './TimesheetCustomField';

interface ITimesheetFormProps {
  formData?: typeof initialTimesheetFormData;
  customFieldsData: Array<ICustomFieldItem> | null;
}

const TimesheetForm: React.FC<ITimesheetFormProps> = ({
  formData = initialTimesheetFormData,
  customFieldsData = [],
}) => {
  const { t } = useTranslation(['timesheetMileage', 'common']);

  const currentUTCPlus14HoursTime = new Date(Date.now() + 14 * 60 * 60 * 1000);

  const [enableFetchBreaks, setEnableFetchBreaks] = useState(false);
  const [timesheetFormData, setTimesheetFormData] = useState<typeof initialTimesheetFormData>(formData);
  const [filteredCustomFields, setFilteredCustomFields] = useState<Array<ICustomFieldItem>>(customFieldsData!);

  const inputCaptionRef = useRef<any>('');

  const navigate = useNavigate();
  const companyInfo = useCompanyInfo();
  const featureList: any = useFeatureList();

  const { isOpen: isCaptionModalOpen, onOpen: onCaptionModalOpen, onClose: onCaptionModalClose } = useDisclosure();

  const { status: recalculateMileageStatus, mutateAsync: mutateAsyncRecalculateMileage } = useRecalculateMileage();
  const { status, mutateAsync: mutateAsyncTimesheet } = useSaveTimesheet();
  const { data: breaksData, fetchStatus: fetchBreakStatus } = useFetchAllBreakRules(
    { active: '1' },
    { enabled: enableFetchBreaks }
  );
  const { mutateAsync: mutateAsyncDeleteBreaks } = useDeleteBreaks();
  const { mutateAsync: muateAsyncUpdateImageCaption, isLoading: isUpdateImageCaptionLoading } = useUpdateImageCaption();
  const { mutateAsync: mutateAsyncDeleteTimesheetImage } = useDeleteTimesheetImage();

  useEffect(() => {
    const updateTimesheetFormData = {
      ...formData,
      jobRequired: companyInfo?.requiresJob || false,
    };

    filterCustomFields({ jobId: formData?.job_id?.id, clockOutDate: formData.end });
    setTimesheetFormData(updateTimesheetFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedEmployee, { search, page, active: '1' });
  };

  const jobLoadOptions = async (search: string, page: any, userId: number) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { search, page, job_status: 1, userId: userId || '' });
  };

  const taskLoadOptions = (page: number, search: string, jobId: any) => {
    if (jobId) {
      return getAsyncSelectLoadOptions(fetchPaginatedTasks, { page, search, task_status: 1, jobId: jobId });
    } else {
      return getAsyncSelectLoadOptions(fetchPaginatedTasks, { page, search, task_status: 1, jobId: '0' });
    }
  };

  const filterCustomFields = ({ jobId, clockOutDate }: { jobId: number; clockOutDate: any }) => {
    if (!customFieldsData?.length) return [];

    const newFilteredCustomFields =
      customFieldsData?.filter(data => {
        let displayOnMatched = true;
        let hasJobId = true;

        if (!isDate(clockOutDate)) {
          displayOnMatched = data.display_on !== TIMESHEET_DISPLAY_TYPES.CLOCK_OUT;
        }

        if (!data.show_for_all_jobs && jobId) {
          hasJobId = data?.jobs?.find((job: { id: any }) => job?.id === jobId) ? true : false;
        }

        return displayOnMatched && hasJobId;
      }) || [];

    setFilteredCustomFields(newFilteredCustomFields);
  };

  const handleClockOutChange = (clockOutDate: Date, values: typeof initialTimesheetFormData) => {
    filterCustomFields({ jobId: values.job_id?.id, clockOutDate: clockOutDate });
  };

  const handleJobChangeCallback = (
    value: any,
    values: typeof initialTimesheetFormData,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (value?.id !== values.job_id?.id) {
      setFieldValue('task_id', '');
    }

    filterCustomFields({ jobId: value?.id, clockOutDate: values.end });
  };

  const handleRecalculateButtonClick = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (formData.id) {
      mutateAsyncRecalculateMileage({ timesheet_ids: [+formData.id] }).then(data => {
        if (!isNaN(data?.payload?.mileage)) {
          const mileage = getDistanceInCompanyFormat(data?.payload?.mileage);
          setFieldValue('mileage', isNumber(mileage) ? mileage.toFixed(2) : 0);
        }
      });
    }
  };

  const handleBreakDuration = (
    startDuration: Date,
    endDuration: Date,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    index: number
  ) => {
    const milliSecondDiff = endDuration?.getTime() - startDuration?.getTime();
    if (milliSecondDiff >= 0) {
      const secondsDiff = Math.floor(milliSecondDiff / 1000);

      const { minutes, seconds } = secondsToMS(secondsDiff ?? 0);

      setFieldValue(`breaks.${index}.duration`, secondsDiff);
      setFieldValue(`breaks.${index}.duration_min`, minutes);
      setFieldValue(`breaks.${index}.duration_sec`, seconds);
    } else {
      toaster.error({
        description: t('timesheetMileage:endBreakTimeCantBeLesserThanStartBreakTime'),
      });
    }
  };

  const validateBreakStart = ({
    breakStart,
    clockIn,
    clockOut,
    setFieldValue,
    index,
    breakEnd,
    fromClockIn = false,
  }: {
    breakStart: Date;
    clockIn: Date;
    clockOut: Date | null;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    index: number;
    breakEnd?: Date;
    fromClockIn?: boolean;
  }) => {
    if (!dateBetweenTwoDates(breakStart, clockIn, clockOut ?? currentUTCPlus14HoursTime)) {
      if (fromClockIn) {
        toaster.error({
          description: 'Clock-in time must be earlier than the break start time',
        });
      } else {
        setFieldValue(`breaks.${index}.startTime`, '');
        setFieldValue(`breaks.${index}.duration`, '');
        setFieldValue(`breaks.${index}.duration_min`, '');
        setFieldValue(`breaks.${index}.duration_sec`, '');
        toaster.error({
          description: t('timesheetMileage:breakInTimeRangeBetweenClockInAndClockOutTime'),
        });
      }
    } else {
      setFieldValue(`breaks.${index}.startTime`, breakStart);
      if (!!breakEnd) {
        if (dateBetweenTwoDates(breakEnd, clockIn, clockOut ?? currentUTCPlus14HoursTime)) {
          handleBreakDuration(breakStart, breakEnd, setFieldValue, index);
        } else {
          toaster.error({
            description: t('timesheetMileage:endBreakTimeCantBeLesserThanStartBreakTime'),
          });
        }
      }
    }
  };

  const validateBreakEnd = ({
    breakEnd,
    breakStart,
    clockIn,
    clockOut,
    setFieldValue,
    index,
    fromClockOut = false,
  }: {
    breakEnd: Date;
    breakStart: Date;
    clockIn: Date;
    clockOut: Date | null;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    index: number;
    fromClockOut?: boolean;
  }) => {
    if (!dateBetweenTwoDates(breakEnd, clockIn, clockOut ?? currentUTCPlus14HoursTime)) {
      if (fromClockOut) {
        toaster.error({
          description: 'Clock-out time must be after the break end time.',
        });
      } else {
        setFieldValue(`breaks.${index}.endTime`, '');
        setFieldValue(`breaks.${index}.duration`, '');
        setFieldValue(`breaks.${index}.duration_min`, '');
        setFieldValue(`breaks.${index}.duration_sec`, '');
        toaster.error({
          description: t('timesheetMileage:breakOutTimeRangeBetweenClockInAndClockOutTime'),
        });
      }
    } else if (breakEnd?.getTime() - breakStart?.getTime() < 0) {
      setFieldValue(`breaks.${index}.endTime`, '');
      setFieldValue(`breaks.${index}.duration`, '');
      setFieldValue(`breaks.${index}.duration_min`, '');
      setFieldValue(`breaks.${index}.duration_sec`, '');
      toaster.error({
        description: t('timesheetMileage:endBreakTimeCantBeLesserThanStartBreakTime'),
      });
    } else {
      setFieldValue(`breaks.${index}.endTime`, breakEnd);
      if (!!breakStart) {
        if (dateBetweenTwoDates(breakStart, clockIn, clockOut ?? currentUTCPlus14HoursTime)) {
          handleBreakDuration(breakStart, breakEnd, setFieldValue, index);
        } else {
          toaster.error({
            description: t('timesheetMileage:breakInTimeRangeBetweenClockInAndClockOutTime'),
          });
        }
      }
    }
  };

  const validateBreaksOnClockIn = (
    clockIn: Date,
    clockOut: Date,
    breaks: any[],
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (clockOut && clockOut?.getTime() - clockIn?.getTime() < 0) {
      toaster.error({ description: 'Clock-in time must be earlier than the clock-out time.' });
    }

    for (let i = 0; i < breaks.length; i++) {
      if (breaks[i]?.setTime?.includes('yes')) {
        validateBreakStart({
          breakStart: breaks[i]?.startTime,
          clockIn: clockIn,
          clockOut: clockOut,
          setFieldValue: setFieldValue,
          index: i,
          fromClockIn: true,
        });
      }
    }
  };

  const validateBreaksOnClockOut = (
    clockIn: Date,
    clockOut: Date,
    breaks: any[],
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (clockIn && clockOut?.getTime() - clockIn?.getTime() < 0) {
      toaster.error({ description: 'Clock-out time must be after the clock-in time.' });
    }

    for (let i = 0; i < breaks.length; i++) {
      if (breaks[i]?.setTime?.includes('yes')) {
        validateBreakEnd({
          breakEnd: breaks[i]?.endTime,
          breakStart: breaks[i]?.startTime,
          clockIn: clockIn,
          clockOut: clockOut,
          setFieldValue: setFieldValue,
          index: i,
          fromClockOut: true,
        });
      }
    }
  };

  const handleCancelClick = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      window.close();
    }
  };

  const handleFormSubmit = (values: typeof initialTimesheetFormData, { setFieldError }: any) => {
    const imagesObject: any = {};

    if (
      values.end instanceof Date &&
      values.start instanceof Date &&
      values.end?.getTime() - values.start?.getTime() < 0
    ) {
      setFieldError('start', { msg: 'Clock-in time must be earlier than the break start time' });
      setFieldError('end', { msg: 'Clock-out time must be after the clock-in time.' });
      return;
    }

    if (values.images.length) {
      values.images.forEach((item: any, index: number) => {
        imagesObject[`image[${index}]`] = item;
        imagesObject[`caption[${index}]`] = values.images_preview[index]?.caption || '';
      });
    }

    const postData: ISaveTimesheetData = {
      id: values.id,
      user_id: values.user_id?.userId ?? null,
      job_id: values.job_id?.id ?? null,
      task_id: values.task_id?.id ?? null,
      start: values.start ? format(new Date(values.start), MONTH_DAY_YEAR_TIME_POST_FORMAT) : '',
      end: values.end ? format(new Date(values.end), MONTH_DAY_YEAR_TIME_POST_FORMAT) : '',
      notes: values.notes,
      breaks: values.breaks
        ? values.breaks.map(({ duration_min, duration_sec, duration, ...item }: any) => {
            const actualDuration = Number(duration_min ?? 0) * 60 + Number(duration_sec ?? 0);

            return {
              ...item,
              duration: actualDuration,
              startTime: item.setTime.includes('yes') ? format(item?.startTime, MONTH_DAY_YEAR_TIME_POST_FORMAT) : '',
              endTime: item.setTime.includes('yes') ? format(item?.endTime, MONTH_DAY_YEAR_TIME_POST_FORMAT) : '',
              break_id: item?.break_id,
              time_break_rule_id: item.time_break_rule_id?.id,
              setTime: item.setTime.includes('yes') ? true : false,
            };
          })
        : [],
      latitude: '',
      longitude: '',
      in_source: '',
      out_source: '',
      mileage: values.mileage ? (values.mileage > 0 ? postDistanceInMiles(values.mileage) : 0) : 0,
      isPto: 0,
      timezone: '',
      lunch_break_duration: values.lunch_break_duration ? values.lunch_break_duration?.value : 0,
      ...imagesObject,
      ...(customFieldsData ? { custom_fields: transformCustomFieldsToPostData(filteredCustomFields, values) } : {}),
      ...(values.is_child_timesheet ? { is_child_timesheet: values.is_child_timesheet } : {}),
    };

    mutateAsyncTimesheet(postData).then(data => {
      if (!data.payload) return;
      handleCancelClick();
    });
  };

  return (
    <div>
      <Title>{timesheetFormData.id ? t('timesheetMileage:updateTimesheet') : t('timesheetMileage:addTimesheet')}</Title>
      <Divider mt="6" />
      <Box mt="10" maxWidth="1000px" mx="auto" pb="100px">
        <Formik
          initialValues={timesheetFormData}
          enableReinitialize
          validationSchema={timesheetFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <SimpleGrid
                columns={{ base: 1, sm: 2 }}
                spacing={{ base: 4, md: 10 }}
                gridRowGap={{ base: 0, md: 0 }}
                mb="6"
              >
                <FormikSelectAsyncPaginate
                  name="user_id"
                  label={t('common:userName')}
                  loadOptions={employeeLoadOptions}
                  selectProps={{
                    placeholder: 'Select user',
                    getOptionLabel: (option: any) => option.firstName + ' ' + option.lastName,
                    getOptionValue: (option: any) => option.userId,
                  }}
                  formControlProps={{ isRequired: true }}
                />
                {companyInfo?.plan?.toLowerCase() !== PLANS.basic && (
                  <>
                    <FormikSelectAsyncPaginate
                      name="job_id"
                      label={t('common:jobName')}
                      loadOptions={(search: any, _: any, { page }: any) =>
                        jobLoadOptions(search, page, values.user_id?.userId)
                      }
                      selectProps={{
                        placeholder: t('common:selectJob'),
                        cacheUniqs: [values.user_id],
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                      }}
                      onChangeCallback={(value: any) => handleJobChangeCallback(value, values, setFieldValue)}
                      formControlProps={{ isRequired: values.jobRequired }}
                    />
                    <FormikSelectAsyncPaginate
                      name="task_id"
                      label={t('common:taskName')}
                      loadOptions={(search: any, _: any, { page }: any) => {
                        return taskLoadOptions(page, search, values.job_id?.id);
                      }}
                      selectProps={{
                        placeholder: t('timesheetMileage:selectTask'),
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                        cacheUniqs: [values.job_id?.id],
                      }}
                    />
                  </>
                )}

                {values.id && !companyInfo?.allowManualBreak ? (
                  <FormikSelect
                    selectProps={{
                      placeholder: t('common:select'),
                    }}
                    name="lunch_break_duration"
                    label={t('timesheetMileage:lunckBreakDuration')}
                    options={lunchBreakDuration}
                  />
                ) : null}

                {/* {values.user_id?.mileageTracking || (values.id && !isNaN(values.mileage)) ? ( */}
                <FormikInput
                  name="mileage"
                  label={t('timesheetMileage:mileageInKmOrMiles', {
                    kmOrMiles: companyInfo?.milesInKm ? 'km' : 'miles',
                  })}
                  type="number"
                  inputElement={
                    values.id ? (
                      <InputRightElement
                        width="125px"
                        cursor="pointer"
                        children={
                          <>
                            <Button
                              size="xs"
                              isLoading={recalculateMileageStatus === 'loading'}
                              onClick={() => handleRecalculateButtonClick(setFieldValue)}
                            >
                              {t('timesheetMileage:recalculate')}
                            </Button>
                            <Popover
                              contentProps={{ right: '100px', top: '94px' }}
                              popoverTriggerEl={
                                <Icon as={IconInfo} ml="2" boxSize="16px" verticalAlign="top" cursor="pointer" />
                              }
                            >
                              <PopoverCloseButton />
                              <PopoverHeader textTransform="uppercase">Note:</PopoverHeader>
                              <PopoverBody>
                                If you have commute mileage turned on, recalculating will override previously calculated
                                compute mileage. Commute mileage will apply if it has not already been run for the day.
                              </PopoverBody>
                            </Popover>
                          </>
                        }
                      />
                    ) : null
                  }
                />
                {/* ) : null} */}
                <FormikDatepicker
                  label={t('common:clockIn')}
                  name="start"
                  datepickerProps={{
                    showTimeInput: true,
                    popperPlacement: 'bottom',
                    dateFormat: companyInfo?.companyDateFormat + ' hh:mm a' || MONTH_DAY_YEAR_TIME_FORMAT,
                    startDate: (values.start as Date) || null,
                    endDate: (values.end as Date) || null,
                    // maxDate: If company allows future clock-in, use values.end; else restrict to allowed future or end date.
                    maxDate: (() => {
                      if (CLOCKIN_FUTURE_DATE_ALLOWED_IDS.includes(companyInfo?.id)) {
                        return values.end ? new Date(values.end) : undefined;
                      }
                      if (values.end && new Date(values.end) < currentUTCPlus14HoursTime) {
                        return new Date(values.end);
                      }
                      return currentUTCPlus14HoursTime;
                    })(),
                    shouldCloseOnSelect: false,
                  }}
                  afterChangeCallback={(clockInDate: Date) =>
                    validateBreaksOnClockIn(clockInDate, values.end as Date, values.breaks || [], setFieldValue)
                  }
                  formControlProps={{ isRequired: true }}
                />
                <FormikDatepicker
                  label={t('common:clockOut')}
                  name="end"
                  datepickerProps={{
                    showTimeInput: true,
                    popperPlacement: 'top',
                    dateFormat: companyInfo?.companyDateFormat + ' hh:mm a' || MONTH_DAY_YEAR_TIME_FORMAT,
                    startDate: (values.start as Date) || null,
                    endDate: (values.end as Date) || null,
                    minDate: (values.start as Date) || null,
                    maxDate: CLOCKIN_FUTURE_DATE_ALLOWED_IDS.includes(companyInfo?.id)
                      ? undefined
                      : currentUTCPlus14HoursTime, // restrict to UTC plus 14 hours
                    shouldCloseOnSelect: false,
                  }}
                  afterChangeCallback={(clockOutDate: Date) => {
                    validateBreaksOnClockOut(values.start as Date, clockOutDate, values.breaks || [], setFieldValue);
                    handleClockOutChange(clockOutDate, values);
                  }}

                  // formControlProps={{ isRequired: true }}
                />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, sm: 2 }}>
                {companyInfo?.allowManualBreak ? (
                  <GridItem colStart={1} colEnd={-1} mb="6">
                    <Text color="gray.700" textTransform="uppercase" mb="2" fontSize="xs" fontWeight="semibold">
                      {t('timesheetMileage:breakDetails')}
                    </Text>
                    <FieldArray
                      name="breaks"
                      render={arrayHelpers => (
                        <div>
                          {values.breaks.map((breakValue: any, index: number) => {
                            return (
                              <React.Fragment key={index}>
                                {index >= 1 ? <Divider mb="3" /> : null}
                                <SimpleGrid
                                  columns={{ base: 1, md: 3 }}
                                  spacingX="3"
                                  key={index}
                                  position="relative"
                                  pr={{ base: '60px', md: '70px' }}
                                >
                                  <FormikSelect
                                    selectProps={{
                                      placeholder: t('timesheetMileage:selectBreakType'),
                                      getOptionLabel: (value: any) => value?.name,
                                      getOptionValue: (value: any) => value?.id,
                                      onMenuOpen: () => {
                                        setEnableFetchBreaks(true);
                                      },
                                      isLoading: fetchBreakStatus === 'fetching',
                                    }}
                                    name={`breaks[${index}].time_break_rule_id`}
                                    label={t('common:type')}
                                    options={breaksData?.payload || []}
                                  />
                                  <Box>
                                    <FormLabel
                                      textAlign="center"
                                      textTransform="uppercase"
                                      color="gray.500"
                                      fontSize="12px"
                                      fontWeight="600"
                                    >
                                      {t('common:duration')}
                                    </FormLabel>
                                    <Flex gridGap="2">
                                      <FormikInput
                                        inputElement={
                                          <InputLeftAddon
                                            children={t('common:mins')}
                                            textTransform="capitalize"
                                            px="3"
                                          />
                                        }
                                        name={`breaks.${index}.duration_min`}
                                        inputProps={{ order: 2, px: '2' }}
                                        inputGroupProps={{ size: 'md' }}
                                        formControlProps={{ isDisabled: breakValue.setTime.includes('yes'), mb: 0 }}
                                      />
                                      <FormikInput
                                        name={`breaks.${index}.duration_sec`}
                                        inputElement={
                                          <InputLeftAddon
                                            children={t('common:secs')}
                                            textTransform="capitalize"
                                            px="3"
                                          />
                                        }
                                        inputProps={{ order: 2, px: '2' }}
                                        inputGroupProps={{ size: 'md' }}
                                        formControlProps={{ isDisabled: breakValue.setTime.includes('yes'), mb: 0 }}
                                      />
                                    </Flex>
                                  </Box>
                                  <FormikCheckbox
                                    options={[{ label: t('timesheetMileage:setBreakStartAndEndTime'), value: 'yes' }]}
                                    name={`breaks.${index}.setTime`}
                                    onChangeCallback={() => {
                                      if (values.breaks[index].startTime && values.breaks[index]?.endTime) {
                                        handleBreakDuration(
                                          values.breaks[index].startTime as Date,
                                          values.breaks[index].endTime as Date,
                                          setFieldValue,
                                          index
                                        );
                                      }
                                    }}
                                    formControlProps={{ alignSelf: 'center' }}
                                  />

                                  {breakValue.setTime.includes('yes') ? (
                                    <>
                                      <FormikDatepicker
                                        label={t('common:startTime')}
                                        name={`breaks.${index}.startTime`}
                                        datepickerProps={{
                                          showTimeInput: true,
                                          popperPlacement: 'top',
                                          dateFormat:
                                            companyInfo?.companyDateFormat + ' hh:mm:ss a' ||
                                            MONTH_DAY_YEAR_TIME_POST_FORMAT,
                                          includeDates: getDaysRange(
                                            values.start as Date,
                                            (values.end as Date | null) ?? currentUTCPlus14HoursTime
                                          ),
                                          shouldCloseOnSelect: false,
                                          isClearable: false,
                                        }}
                                        handleChangeCallback={(startDate: Date) =>
                                          validateBreakStart({
                                            breakStart: startDate,
                                            clockIn: values.start as Date,
                                            clockOut: values.end as Date,
                                            setFieldValue: setFieldValue,
                                            index: index,
                                            breakEnd: values.breaks[index]?.endTime as Date | undefined,
                                          })
                                        }
                                        // formControlProps={{ isRequired: true }}
                                      />
                                      <FormikDatepicker
                                        label={t('common:endTime')}
                                        name={`breaks.${index}.endTime`}
                                        datepickerProps={{
                                          showTimeInput: true,
                                          popperPlacement: 'top',
                                          dateFormat:
                                            companyInfo?.companyDateFormat + ' hh:mm:ss a' ||
                                            MONTH_DAY_YEAR_TIME_POST_FORMAT,
                                          includeDates: removeDaysRange(
                                            getDaysRange(
                                              values.start as Date,
                                              (values.end as Date | null) ?? currentUTCPlus14HoursTime
                                            ),
                                            values.breaks[index].startTime
                                          ),
                                          shouldCloseOnSelect: false,
                                          isClearable: false,
                                        }}
                                        formControlProps={{
                                          isDisabled: !values.breaks[index]?.startTime,
                                        }}
                                        handleChangeCallback={(endDate: Date) =>
                                          validateBreakEnd({
                                            breakEnd: endDate,
                                            breakStart: values.breaks[index].startTime as Date,
                                            clockIn: values.start as Date,
                                            clockOut: values.end as Date,
                                            setFieldValue: setFieldValue,
                                            index: index,
                                          })
                                        }
                                        // formControlProps={{ isRequired: true }}
                                      />
                                    </>
                                  ) : null}

                                  <Box alignSelf="center" position="absolute" top="0" right="0">
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => {
                                        if (breakValue.break_id) {
                                          mutateAsyncDeleteBreaks({ break_ids: [breakValue.break_id] }).then(() => {
                                            arrayHelpers.remove(index);
                                          });
                                        } else {
                                          arrayHelpers.remove(index);
                                        }
                                      }}
                                    >
                                      <Icon as={IconClose} sx={{ '& path': { fill: 'white' } }} />
                                    </Button>
                                  </Box>
                                </SimpleGrid>
                              </React.Fragment>
                            );
                          })}

                          <Button
                            type="button"
                            display="block"
                            onClick={() =>
                              arrayHelpers.push({
                                break_id: '',
                                time_break_rule_id: '',
                                setTime: 'no',
                                startTime: '',
                                endTime: '',
                                duration: '',
                              })
                            }
                          >
                            {t('timesheetMileage:addBreak')}
                          </Button>
                        </div>
                      )}
                    />
                  </GridItem>
                ) : null}

                {/*  Images */}
                {featureList[FEATURES.photo_attachments] && (
                  <GridItem colSpan={2}>
                    {values.images_preview.length ? (
                      <Text color="gray.500" textTransform="uppercase" mb="2">
                        {t('timesheetMileage:images')}
                      </Text>
                    ) : null}
                    <Flex alignItems="center" flexWrap="wrap">
                      {values.images_preview.length
                        ? values.images_preview.map((item: any, index: number) => (
                            <Tooltip label={item.caption} key={index}>
                              <Box
                                position="relative"
                                mr="2"
                                cursor="pointer"
                                border="1px solid"
                                borderColor="primary.900"
                              >
                                <Image
                                  borderRadius="sm"
                                  key={item.imageUrl}
                                  boxSize={{ base: '100px', md: '160px' }}
                                  objectFit="cover"
                                  src={item.imageUrl}
                                  onClick={() => {
                                    setFieldValue('islightBoxOpen', true);
                                    setFieldValue('imageIndex', index);
                                  }}
                                />
                                <Icon
                                  as={IconClose}
                                  position="absolute"
                                  top="0"
                                  right="0"
                                  bg="secondary.700"
                                  p="1"
                                  boxSize="24px"
                                  cursor="pointer"
                                  onClick={() => {
                                    if (item.id) {
                                      mutateAsyncDeleteTimesheetImage(item.id).then(() => {
                                        const updatedImages = values.images.filter(
                                          (_: any, itemIndex: number) => index !== itemIndex
                                        );
                                        const updatedImagePreviews = values.images_preview.filter(
                                          (_: any, itemIndex: number) => index !== itemIndex
                                        );
                                        setFieldValue('images', updatedImages);
                                        setFieldValue('images_preview', updatedImagePreviews);
                                      });
                                    } else {
                                      const updatedImages = values.images.filter(
                                        (_: any, itemIndex: number) => index !== itemIndex
                                      );
                                      const updatedImagePreviews = values.images_preview.filter(
                                        (_: any, itemIndex: number) => index !== itemIndex
                                      );
                                      setFieldValue('images', updatedImages);
                                      setFieldValue('images_preview', updatedImagePreviews);
                                    }
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ))
                        : null}
                      <FormLabel htmlFor="clockin_image" fontSize="sm" display="inline-block" cursor="pointer">
                        {values.images.length < 5 ? (
                          <Button
                            color="primary.500"
                            fontWeight="semibold"
                            background="rgba(38, 103, 201, 0.1)"
                            _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                            _active={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                            pointerEvents="none"
                            size={values.images_preview.length ? 'sm' : 'md'}
                            {...(values.images_preview.length ? {} : { leftIcon: <Icon as={IconPicture} /> })}
                          >
                            {!values.images_preview.length ? t('timesheetMileage:addImage') : <Icon as={IconPlus} />}
                          </Button>
                        ) : null}
                        <Input
                          id="clockin_image"
                          accept="image/*"
                          multiple
                          display="none"
                          type="file"
                          name="clockin_image"
                          onChange={(event: any) => {
                            const files = event.target.files;
                            const newImages = [...values.images, ...files];

                            if (newImages.length > 5) {
                              toaster.error({ description: t('timesheetMileage:selectImagesLessThan5') });
                              return;
                            }

                            if (files) {
                              const previewFiles = [...files].map((item: any) => ({
                                id: undefined,
                                caption: '',
                                imageUrl: URL.createObjectURL(item),
                              }));
                              setFieldValue('images_preview', [...values.images_preview, ...previewFiles]);
                              setFieldValue('images', newImages);
                            }
                          }}
                        />
                      </FormLabel>
                    </Flex>
                  </GridItem>
                )}

                {/* lightBox */}
                {values.islightBoxOpen ? (
                  <>
                    <Lightbox
                      mainSrc={values.images_preview[values.imageIndex].imageUrl}
                      nextSrc={values.images_preview[(values.imageIndex + 1) % values.images_preview.length].imageUrl}
                      prevSrc={
                        values.images_preview[
                          (values.imageIndex + values.images_preview.length - 1) % values.images_preview.length
                        ].imageUrl
                      }
                      onCloseRequest={() => setFieldValue('islightBoxOpen', false)}
                      onMovePrevRequest={() => {
                        const newImageIndex =
                          (values.imageIndex + values.images_preview.length - 1) % values.images_preview.length;
                        setFieldValue('imageIndex', newImageIndex);
                      }}
                      imageCaption={values.images_preview[values.imageIndex].caption}
                      toolbarButtons={[
                        <Button variant="solid" size="sm" onClick={onCaptionModalOpen}>
                          {values.images_preview[values.imageIndex].caption ? t('common:edit') : t('common:add')}{' '}
                          Caption
                        </Button>,
                      ]}
                      onMoveNextRequest={() => {
                        const newImageIndex = (values.imageIndex + 1) % values.images_preview.length;
                        setFieldValue('imageIndex', newImageIndex);
                      }}
                    />
                    <ControllableModal isOpen={isCaptionModalOpen} onClose={onCaptionModalClose}>
                      <ModalContent>
                        <ModalHeader>
                          {values.images_preview[values.imageIndex].caption ? t('common:edit') : t('common:add')}{' '}
                          Caption
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                          <Input
                            variant="filled"
                            border="1px"
                            borderColor="inherit"
                            fontSize="sm"
                            defaultValue={values.images_preview[values.imageIndex].caption}
                            // onChange={() => null}
                            ref={inputCaptionRef}
                          />
                        </ModalBody>
                        <ModalFooter>
                          <Button
                            size="sm"
                            isLoading={isUpdateImageCaptionLoading}
                            onClick={() => {
                              if (!inputCaptionRef?.current) return;

                              if (values.images_preview[values.imageIndex].id) {
                                const postData = {
                                  id: values.images_preview[values.imageIndex].id,
                                  parameters: {
                                    caption: inputCaptionRef?.current?.value,
                                  },
                                };

                                muateAsyncUpdateImageCaption(postData).then(() => {
                                  setFieldValue(
                                    `images_preview[${values.imageIndex}].caption`,
                                    inputCaptionRef?.current?.value
                                  );
                                  onCaptionModalClose();
                                });
                              } else {
                                setFieldValue(
                                  `images_preview[${values.imageIndex}].caption`,
                                  inputCaptionRef?.current?.value
                                );
                                onCaptionModalClose();
                              }
                            }}
                          >
                            {t('common:save')}
                          </Button>
                        </ModalFooter>
                      </ModalContent>
                    </ControllableModal>{' '}
                  </>
                ) : null}

                <Box mt="3">
                  <FormikTextArea name="notes" label={t('common:notes')} textareaProps={{ size: 'xl' }} />
                </Box>
              </SimpleGrid>

              {Array.isArray(filteredCustomFields) && filteredCustomFields.length ? (
                <>
                  <Text color="gray.700" textTransform="uppercase" mb="2" fontSize="xs" fontWeight="semibold">
                    Custom Fields
                  </Text>
                  <TimesheetCustomField
                    customFieldsData={filteredCustomFields}
                    isChildTimesheet={values.is_child_timesheet}
                  />
                </>
              ) : null}
              <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                {t('common:save')}
              </Button>
              <Button
                bg="gray.600"
                _hover={{ bg: 'gray.700' }}
                _active={{ bg: 'gray.700' }}
                ml="5"
                onClick={handleCancelClick}
              >
                {t('common:cancel')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </div>
  );
};

export default TimesheetForm;
